import { useMemo, useState } from 'react';
import { Dropdown } from 'sharedUi/Dropdown';
import {
  missingRequired,
  type IntegrationConfigField,
  type IntegrationFieldSpec,
} from '../../services/app-config.service';

const input =
  'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

type ConfigObject = Record<string, unknown>;

/** `null` and `''` both mean "not set" — the resolver treats them as inherit. */
const isBlank = (value: unknown) => value === null || value === undefined || value === '';

const Field = ({ label, hint, required, missing, children }: {
  label: string; hint?: string | null; required?: boolean; missing?: boolean;
  children: React.ReactNode;
}) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </label>
    {children}
    {missing
      ? <p className="mt-1 text-caption text-red-600 dark:text-red-400">Required.</p>
      : hint && <p className="mt-1 text-caption text-gray-500">{hint}</p>}
  </div>
);

/**
 * The editor for `Integration.<Channel>.<Provider>.Config` — one labelled field per
 * property the channel reads, instead of hand-written JSON.
 *
 * Three rules it keeps:
 *  - **A blank field is stored as null**, never as an empty string.
 *  - **Properties it does not know about are preserved.** The description
 *    describes, it does not restrict: anything already in the object that has no
 *    field here is kept exactly as it was, and is listed underneath.
 *  - **Required means a SEND needs it**, not that the form is being fussy. Some
 *    are conditional — the OAuth client id matters only while the account
 *    authenticates with OAuth 2.0 — which `requiredWhen` carries.
 */
const IntegrationConfigEditor = ({ spec, value, onChange, disabled }: {
  spec: IntegrationFieldSpec;
  value: ConfigObject;
  onChange: (next: ConfigObject) => void;
  disabled?: boolean;
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const known = useMemo(() => new Set(spec.fields.map((f) => f.name)), [spec.fields]);
  const extras = useMemo(
    () => Object.keys(value || {}).filter((k) => !known.has(k)),
    [value, known]);

  const set = (name: string, next: unknown) => onChange({ ...value, [name]: next });

  /** Required for the values as they stand — a conditional one only counts while
   *  its condition holds, so switching to Password stops demanding a client id. */
  const isRequired = (field: IntegrationConfigField) => field.required
    || (!!field.requiredWhen && value?.[field.requiredWhen.field] === field.requiredWhen.equals);

  const missing = useMemo(
    () => new Set(missingRequired(spec.fields, value).map((f) => f.name)),
    [spec.fields, value]);

  const render = (field: IntegrationConfigField) => {
    const current = value?.[field.name];

    /* Both pickers: empty is empty — no "not set" entry in the list. The clear
       button is how a value is taken back out, and it stores null. */
    if (field.type === 'boolean') {
      return (
        <Field key={field.name} label={field.label} hint={field.hint} required={isRequired(field)} missing={missing.has(field.name)}>
          <Dropdown
            value={isBlank(current) ? null : Boolean(current)}
            options={[{ label: 'Yes', value: true }, { label: 'No', value: false }]}
            onChange={(e: never) => set(field.name, (e as { value: boolean | null }).value ?? null)}
            placeholder="Select"
            showClear
            disabled={disabled}
            className="w-full"
            data-testid={`IntegrationConfig.Input.${field.name}`}
          />
        </Field>
      );
    }

    if (field.type === 'select') {
      return (
        <Field key={field.name} label={field.label} hint={field.hint} required={isRequired(field)} missing={missing.has(field.name)}>
          <Dropdown
            value={isBlank(current) ? null : String(current)}
            options={field.options || []}
            onChange={(e: never) => set(field.name, (e as { value: string | null }).value || null)}
            placeholder="Select"
            showClear
            disabled={disabled}
            className="w-full"
            data-testid={`IntegrationConfig.Input.${field.name}`}
          />
        </Field>
      );
    }

    if (field.type === 'array') {
      const text = Array.isArray(current) ? (current as unknown[]).join(', ') : '';
      return (
        <Field key={field.name} label={field.label} hint={field.hint || 'Comma separated.'} required={isRequired(field)} missing={missing.has(field.name)}>
          <input className={input} value={text} disabled={disabled}
            placeholder={field.placeholder || ''}
            onChange={(e) => {
              const parts = e.target.value.split(',').map((p) => p.trim()).filter(Boolean);
              set(field.name, parts.length ? parts : null);
            }}
            data-testid={`IntegrationConfig.Input.${field.name}`} />
        </Field>
      );
    }

    const isNumber = field.type === 'number';
    return (
      <Field key={field.name} label={field.label} hint={field.hint} required={isRequired(field)} missing={missing.has(field.name)}>
        <input
          className={input}
          value={isBlank(current) ? '' : String(current)}
          placeholder={field.placeholder || ''}
          inputMode={isNumber ? 'numeric' : undefined}
          disabled={disabled}
          onChange={(e) => {
            const raw = isNumber ? e.target.value.replace(/[^0-9]/g, '') : e.target.value;
            if (raw === '') return set(field.name, null);
            set(field.name, isNumber ? Number(raw) : raw);
          }}
          data-testid={`IntegrationConfig.Input.${field.name}`}
        />
      </Field>
    );
  };

  const basic = spec.fields.filter((f) => !f.advanced);
  const advanced = spec.fields.filter((f) => f.advanced);

  return (
    <div className="space-y-4" data-testid="IntegrationConfig.Editor">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{basic.map(render)}</div>

      {advanced.length > 0 && (
        <div className="rounded border border-dashed border-gray-300 p-3 dark:border-gray-600">
          <button type="button" className="text-xs font-medium text-blue-600 hover:underline"
            onClick={() => setShowAdvanced((v) => !v)}
            data-testid="IntegrationConfig.Button.Advanced">
            {showAdvanced ? 'Hide' : 'Show'} advanced settings ({advanced.length})
          </button>
          {showAdvanced && (
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">{advanced.map(render)}</div>
          )}
        </div>
      )}

      {extras.length > 0 && (
        <p className="text-caption text-gray-500">
          Kept as they are: <span className="font-mono">{extras.join(', ')}</span>. Nothing
          here removes a property this screen does not describe.
        </p>
      )}

      {missing.size > 0 && (
        <p className="text-caption text-red-600 dark:text-red-400"
           data-testid="IntegrationConfig.Missing">
          Still needed: {spec.fields.filter((f) => missing.has(f.name))
            .map((f) => f.label).join(', ')}.
        </p>
      )}

      {spec.secretKeys.length > 0 && (
        <p className="text-caption text-gray-500">
          Credentials are not set here — they are their own encrypted configurations:{' '}
          <span className="font-mono">{spec.secretKeys.map((s) => s.key).join(', ')}</span>.
        </p>
      )}
    </div>
  );
};

export default IntegrationConfigEditor;
